<script setup>
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { apiRequest, assertOk, errorMessage } from '@/api/http';
import { useToast } from '@/composables/useToast';
import { WEEKDAYS, formatTimeRange } from '@/utils/format';
import { SCHOOL_DAY_ROWS } from '@/constants/schoolDay';
import { required } from '@/utils/validation';

// Add or edit one class period in a section's weekly schedule. Subject comes
// first and narrows the teachers to those who can take it. Adding can cover
// several days at once, and the times start right after the section's last
// period on those days. Times use the browser's time input (no picker dialog
// on top of this one).
const props = defineProps({
  item: { type: Object, default: null }, // null = add
  grade: { type: String, required: true }, // e.g. 'Grade 7'
  section: { type: Object, default: null }, // { id, room_section }
  // The section's periods already on the schedule, for the suggested times.
  periods: { type: Array, default: () => [] },
  // The term the page shows (a school year graded in terms has a schedule
  // per term); new periods go into it. Null in a quarter year. An edited
  // period keeps its term.
  term: { type: String, default: null },
  // Admins add with AddScheduleAdmin; advisers use addSchedule.
  addEndpoint: { type: String, default: '/enroll-student/AddScheduleAdmin' },
});
const open = defineModel({ type: Boolean, default: false });
const emit = defineEmits(['saved']);

const store = useStore();
const toast = useToast();
const formRef = ref(null);
const saving = shallowRef(false);
const loadingOptions = shallowRef(false);
// [{ id, subject_title, teachers: [{ id, name }] }]
const options = shallowRef([]);
const form = reactive({ days: [], from: '', to: '', subject: null, teacher: null });
// Once the user types a time, suggestions stop overwriting it.
const fromTouched = shallowRef(false);
const toTouched = shallowRef(false);

const hhmm = (t) => String(t || '').slice(0, 5);
const toMinutes = (t) => {
  const [h, m] = hhmm(t).split(':').map(Number);
  return h * 60 + m;
};
const fromMinutes = (n) => `${String(Math.floor(n / 60) % 24).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
const hours = computed(() =>
  form.from && form.to ? (toMinutes(form.to) - toMinutes(form.from)) / 60 : 0,
);
const atLeastAnHour = () =>
  !form.from || !form.to || hours.value >= 1 || 'Must be at least 1 hour after the start.';
const daysRule = () => form.days.length > 0 || 'Choose at least one day.';

// Editing changes one period, so it keeps to one day.
const dayModel = computed({
  get: () => (props.item ? form.days[0] ?? null : form.days),
  set: (value) => {
    form.days = value == null ? [] : Array.isArray(value) ? value : [value];
  },
});
const everyWeekday = computed(() => WEEKDAYS.every((d) => form.days.includes(d)));
function toggleWeekdays() {
  form.days = everyWeekday.value ? [] : [...WEEKDAYS];
}

const subject = computed(() => options.value.find((s) => s.id === Number(form.subject)) ?? null);
const teachers = computed(() => {
  const list = subject.value?.teachers ?? [];
  // A period whose teacher no longer qualifies still names them rather
  // than showing an id.
  const i = props.item;
  if (i?.teacherID != null && Number(form.subject) === Number(i.subjectId) && !list.some((t) => t.id === Number(i.teacherID))) {
    const name = [i.fname, i.lname].filter(Boolean).join(' ');
    if (name) return [{ id: Number(i.teacherID), name }, ...list];
  }
  return list;
});
const noTeacher = computed(() => !!subject.value && teachers.value.length === 0);
const subjectHint = (s) =>
  s.teachers.length === 0 ? 'No teacher yet' : s.teachers.length === 1 ? s.teachers[0].name : `${s.teachers.length} teachers`;

async function loadOptions() {
  loadingOptions.value = true;
  try {
    const rows = await apiRequest('get', `/subjects/scheduleOptions/${encodeURIComponent(props.grade)}`);
    options.value = Array.isArray(rows) ? rows : [];
  } catch {
    options.value = [];
  } finally {
    loadingOptions.value = false;
  }
  // A period saved with a subject that no longer fits this grade asks for a
  // new one.
  if (form.subject != null && !subject.value) form.subject = null;
}

function onSubjectChange() {
  // With only one teacher who can take the subject, pick them.
  const list = teachers.value;
  form.teacher = list.length === 1 ? list[0].id : list.some((t) => t.id === form.teacher) ? form.teacher : null;
}

// Start right after the section's last period on the chosen days, for an
// hour; back-to-back periods then need no typing.
function suggestTimes() {
  if (props.item) return;
  const ends = props.periods
    .filter((p) => form.days.includes(p.day) && p.times_slot_to)
    .map((p) => toMinutes(p.times_slot_to));
  if (!fromTouched.value) form.from = ends.length ? fromMinutes(Math.max(...ends)) : '';
  if (!toTouched.value) form.to = form.from ? fromMinutes(toMinutes(form.from) + 60) : '';
}
watch(() => form.days, suggestTimes);

function onFromInput(value) {
  fromTouched.value = true;
  if (!toTouched.value) form.to = value ? fromMinutes(toMinutes(value) + 60) : '';
}

watch(open, (isOpen) => {
  if (!isOpen) return;
  const i = props.item;
  fromTouched.value = !!i;
  toTouched.value = !!i;
  form.days = i?.day ? [i.day] : [];
  form.from = hhmm(i?.times_slot_from);
  form.to = hhmm(i?.times_slot_to);
  form.subject = i?.subjectId != null ? Number(i.subjectId) : null;
  form.teacher = i?.teacherID != null ? Number(i.teacherID) : null;
  formRef.value?.resetValidation();
  loadOptions();
});

async function save() {
  const { valid } = await formRef.value.validate();
  if (!valid) return;
  saving.value = true;
  try {
    const data = {
      teacherID: form.teacher,
      subjectId: form.subject,
      roomId: props.section?.id,
      times_slot_from: form.from,
      times_slot_to: form.to,
      // Adding sends every chosen day; the server saves one period per day.
      day: props.item ? form.days[0] : form.days,
      grade_level: props.grade,
      hours: hours.value,
      school_yearId: store.getters.getFilterSelected,
      term: props.term,
    };
    const body = props.item
      ? await apiRequest('patch', `/enroll-student/updateClassProgram/${props.item.availId}`, data)
      : await apiRequest('post', props.addEndpoint, data);
    assertOk(body);
    toast.success(props.item ? 'Schedule updated.' : body?.msg || 'Schedule added.');
    open.value = false;
    emit('saved');
  } catch (e) {
    toast.error(errorMessage(e, 'Could not save the schedule.'));
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="item ? 'Edit class period' : 'Add class period'"
    :description="section ? `${grade} · ${section.room_section}` : grade"
    size="md"
  >
    <v-form ref="formRef" class="form-grid" @submit.prevent="save">
      <UiFormField label="Subject" class="span-2" required v-slot="{ id }">
        <v-autocomplete
          :id="id"
          v-model="form.subject"
          :items="options"
          item-title="subject_title"
          item-value="id"
          :loading="loadingOptions"
          placeholder="Select a subject"
          :no-data-text="`No ${grade} subjects yet.`"
          :rules="[required]"
          hide-details="auto"
          @update:model-value="onSubjectChange"
        >
          <template #item="{ props: itemProps, item: option }">
            <v-list-item v-bind="itemProps" :subtitle="subjectHint(option.raw)" />
          </template>
        </v-autocomplete>
      </UiFormField>

      <UiFormField label="Teacher" class="span-2" required v-slot="{ id }">
        <v-alert v-if="noTeacher" type="info" variant="tonal" density="compact" icon="mdi-information-outline">
          No teacher handles {{ subject.subject_title }} in {{ grade }} yet. Teachers add their grade levels
          and subjects under Settings → Teaching.
        </v-alert>
        <v-autocomplete
          v-else
          :id="id"
          v-model="form.teacher"
          :items="teachers"
          item-title="name"
          item-value="id"
          :disabled="!form.subject"
          :placeholder="form.subject ? 'Select a teacher' : 'Choose a subject first'"
          :rules="[required]"
          hide-details="auto"
        />
      </UiFormField>

      <UiFormField
        :label="item ? 'Day' : 'Days'"
        class="span-2"
        :description="item ? '' : 'Pick every day this class meets; one period is added for each.'"
        required
      >
        <v-input :model-value="form.days" :rules="[daysRule]" hide-details="auto">
          <div class="schedule-days">
            <v-chip-group v-model="dayModel" :multiple="!item" selected-class="schedule-days__on" column>
              <v-chip v-for="day in WEEKDAYS" :key="day" :value="day" variant="outlined" filter>
                {{ day.slice(0, 3) }}
              </v-chip>
            </v-chip-group>
            <v-btn v-if="!item" variant="text" size="small" @click="toggleWeekdays">
              {{ everyWeekday ? 'Clear' : 'Every weekday' }}
            </v-btn>
          </div>
        </v-input>
      </UiFormField>

      <UiFormField label="Starts" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.from"
          type="time"
          :rules="[required]"
          hide-details="auto"
          @update:model-value="onFromInput"
        />
      </UiFormField>
      <UiFormField label="Ends" required v-slot="{ id }">
        <v-text-field
          :id="id"
          v-model="form.to"
          type="time"
          :rules="[required, atLeastAnHour]"
          hide-details="auto"
          @update:model-value="toTouched = true"
        />
      </UiFormField>

      <!-- The school day's fixed blocks, so periods are planned around them. -->
      <div class="school-day span-2">
        <div class="school-day__title">Reserved times</div>
        <dl class="school-day__list">
          <template v-for="row in SCHOOL_DAY_ROWS" :key="row.time">
            <dt class="text-no-wrap">{{ formatTimeRange(row.time) }}</dt>
            <dd>{{ row.label }}</dd>
          </template>
        </dl>
      </div>
    </v-form>

    <template #footer="{ close }">
      <v-btn variant="outlined" @click="close">Cancel</v-btn>
      <v-btn color="primary" :loading="saving" :disabled="noTeacher" @click="save">
        {{ item ? 'Save changes' : form.days.length > 1 ? `Add ${form.days.length} periods` : 'Add period' }}
      </v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.schedule-days {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
}
.schedule-days__on {
  border-color: var(--foreground);
  background-color: var(--subtle);
}
.school-day {
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.8125rem;
}
.school-day__title {
  font-weight: 600;
  margin-bottom: 6px;
}
.school-day__list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 16px;
  margin: 0;
}
.school-day__list dt {
  font-weight: 500;
}
.school-day__list dd {
  margin: 0;
  color: var(--muted-foreground);
}
</style>
